import { useEffect, useState, useRef } from 'react';

/**
 * Custom hook for managing modal state via URL hash
 * A modal dialog can be opened by clicking a link that sets the URL hash to the modal ID.
 *
 * This enables a semantically valid string that contains an href to open a modal dialog
 * also browser back button support and shareable URLs.
 * Automatically tracks the hash before the modal opens and restores it on close.
 *
 * @param modalId - The unique identifier for the modal (used in hash)
 * @returns Object with modal state and control functions
 *
 * @example
 * ```tsx
 * const accountHelpModal = useModalHash('account-help');
 *
 * In the component add a link to open the modal
 * <a href={accountHelpModal.getHashLink()}>Need help?</a>
 *
 * // Add the modal itself in component render near root
 * <ModalDialog
 *   isOpen={accountHelpModal.isOpen}
 *   onRequestClose={accountHelpModal.close}
 * >
 *   Content
 * </ModalDialog>
 * ```
 *
 * When the modal is open, the URL will have `#account-help` appended.
 * Closing the modal will restore the previous hash (or remove it if none).
 */
export const useModalHash = (modalId: string) => {
  // Track current hash state
  const [currentHash, setCurrentHash] = useState('');

  // Store the hash that was active before the modal opened
  const preModalHashRef = useRef<string>('');

  // Update hash on mount and when URL changes
  useEffect(() => {
    const updateHash = () => {
      const newHash = globalThis.location.hash.slice(1); // Remove the '#'

      // Store previous hash BEFORE updating current
      // Only if we're transitioning TO the modal (not already in it)
      if (newHash === modalId && currentHash !== modalId) {
        preModalHashRef.current = currentHash;
      }

      setCurrentHash(newHash);
    };

    // Set initial hash
    updateHash();

    // Listen for hash changes (when user clicks links or uses back/forward)
    globalThis.addEventListener('hashchange', updateHash);

    return () => {
      globalThis.removeEventListener('hashchange', updateHash);
    };
  }, [currentHash, modalId]);

  // Check if this modal is currently open
  const isOpen = currentHash === modalId;

  /**
   * Close the modal by restoring the hash from before the modal opened
   * If modal was opened directly (e.g., via URL), this will remove the hash
   */
  const close = () => {
    // Restore the hash from before the modal opened
    // If modal was opened directly, preModalHashRef will be empty
    globalThis.location.hash = preModalHashRef.current || '';
  };

  /**
   * Get the hash link for this modal
   * Useful for interpolating into translated strings
   *
   * @returns Hash link string (e.g., '#account-help')
   */
  const getHashLink = () => `#${modalId}`;

  return {
    isOpen,
    close,
    getHashLink,
  };
};
